import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Dialog, Switch } from '@headlessui/react';
import { ArchiveBoxIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TWModal } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import publicCouponApi from '../../../services/publicCouponApi';
import storeApi from '../../../services/storeApi';
import menuApi from '../../../services/menuApi';
import { apiMessage } from '../../connect/connect-api-message';
import FinBadge from '../../connect/finance/fin-badge';
import FinButton from '../../connect/finance/fin-button';
import RuleSentence from './campaign/RuleSentence';
import CodeField, { useCodeCheck } from './campaign/CodeField';
import AdvancedSettings from './campaign/AdvancedSettings';
import ConfirmDialog, { EDIT_DISCARD_COPY } from './campaign/ConfirmDialog';
import { menusForStore } from './coupon-menus';
import {
  ALL_STORES,
  CODE_FORMAT_MESSAGES,
  MESSAGES,
  TYPE_LABELS,
  codeFormatError,
  isTakenMessage,
  itemOf,
  pickStore,
  storeChoices,
  summarySentence,
  validateChangedRule,
} from '../lib/campaign-rule';
import { formFromCoupon, upsertBody } from '../lib/campaign-body';
import { CAMPAIGN_STATUS, statusAfterSwitch, switchOn } from '../lib/campaign-status';

// The one-screen Edit campaign modal (spec 2026-10-21 §2.5, handoff §7,
// mock 1b-edit): 880px, two columns. Left: the Rule (the step 2 sentence,
// compact), Basics (name, code) and Advanced settings (open). Right
// (#FAFBFD fin-recess): the summary sentence, the code, Status and Used,
// and the note. Nothing regenerates; the rule is checked only where a field
// changed (Review Focus 3), so an old campaign still saves its status or
// name. Below sm the summary drops under the form.
export const EDIT_COPY = {
  title: 'Edit campaign',
  rule: 'Rule',
  basics: 'Basics',
  name: 'Campaign name',
  summary: 'Summary',
  status: 'Status',
  active: 'Active',
  inactive: 'Inactive',
  draft: 'Draft',
  archived: 'Archived',
  switchLabel: 'Campaign active',
  used: 'Used',
  times: (n) => `${n} ${n === 1 ? 'time' : 'times'}`,
  note: 'Inactive campaigns stay in the list but can’t be redeemed. Archiving removes them from the active list.',
  archive: 'Archive campaign',
  cancel: 'Cancel',
  save: 'Save changes',
  saveDraft: 'Save draft',
  publish: 'Publish',
  close: 'Close',
  archiveTitle: 'Archive this campaign?',
  archiveBody: 'It leaves the active list and can’t be redeemed. An archived campaign can’t be restored.',
  saveFailed: 'Couldn’t save the campaign. Try again.',
  storeGone: 'Store no longer available',
};

// The names a List row carries for its product (spec §1.6): the summary and
// the rule's labels use them while ProductChoices is away, or when the
// product left the menus.
const namesOf = (coupon) => {
  if (coupon?.type === 0) return { product: coupon.freeitemproductname || '', item: coupon.freeitemname || '' };
  if (coupon?.type === 2) return { product: coupon.getproductname || '', item: '' };
  return {};
};

const keepMenusOf = (menus, storeid, menuids) => {
  const visible = menusForStore(menus, storeid);
  return menuids.filter((id) => !menus.some((menu) => menu.id === id) || visible.some((menu) => menu.id === id));
};

const EditCampaignModal = ({ open, coupon, companyId, onClose, onSaved, onOpenOwner }) => {
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [stores, setStores] = useState([]);
  const [storesState, setStoresState] = useState('loading');
  const [menus, setMenus] = useState([]);
  const [choices, setChoices] = useState([]);
  const [choicesState, setChoicesState] = useState('loading');
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [footerError, setFooterError] = useState('');
  const [serverCodeError, setServerCodeError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const nameErrorId = useId();

  // The stored row's company: the api refuses a different one (spec §1.4).
  const ownerCompany = coupon?.companyid || companyId;

  // An answer applies only if it is the latest request: closing, or opening
  // another campaign or company (the effect's cleanup), retires the one in flight.
  const choicesRequest = useRef(0);
  const loadChoices = useCallback(async () => {
    const id = ++choicesRequest.current;
    setChoicesState('loading');
    try {
      const res = await publicCouponApi.productChoices(ownerCompany);
      if (id !== choicesRequest.current) return;
      setChoices(res?.data?.data || []);
      setChoicesState('ready');
    } catch {
      if (id !== choicesRequest.current) return;
      setChoices([]);
      setChoicesState('failed');
    }
  }, [ownerCompany]);

  useEffect(() => {
    if (!open || !coupon) return undefined;
    let live = true;
    const next = formFromCoupon(coupon);
    setInitial(next);
    setForm(next);
    setAdvancedOpen(true);
    setSaving(false);
    setFooterError('');
    setServerCodeError(null);
    setConfirm(null);
    setStores([]);
    setStoresState('loading');
    storeApi
      .list(`companyid=${ownerCompany}`)
      .then((res) => {
        if (!live) return;
        setStores(res?.data?.data || []);
        setStoresState('ready');
      })
      .catch(() => {
        if (!live) return;
        setStores([]);
        setStoresState('failed');
      });
    menuApi
      .list(`?companyid=${ownerCompany}`)
      .then((res) => {
        if (live) setMenus(res?.data?.data || []);
      })
      .catch(() => {
        if (live) setMenus([]);
      });
    loadChoices();
    return () => {
      live = false;
      choicesRequest.current += 1;
    };
  }, [open, coupon, ownerCompany, loadChoices]);

  const rule = form?.rule || null;
  const ruleType = rule?.type || null;
  const archived = initial?.status === CAMPAIGN_STATUS.archived;
  const draft = initial?.status === CAMPAIGN_STATUS.draft;

  // A free item's product is read off ProductChoices by its item; it is not
  // stored, so it never makes the form dirty.
  const productId = rule
    ? rule.productId ||
      (rule.type === 'free_item'
        ? choices.find((choice) => (choice.items || []).some((entry) => entry.id === rule.itemId))?.productid || null
        : null)
    : null;
  const viewRule = rule && productId !== rule.productId ? { ...rule, productId } : rule;
  const product = productId ? choices.find((choice) => choice.productid === productId) || null : null;
  const item = itemOf(product, rule?.itemId);
  const typeProducts = useMemo(
    () => (ruleType === 'free_item' ? choices.filter((choice) => (choice.items || []).length > 0) : choices),
    [ruleType, choices],
  );
  const names = namesOf(coupon);

  const ruleErrors = initial && viewRule ? validateChangedRule(initial.rule, viewRule, product) : {};
  const codeChanged = Boolean(form && initial && form.code !== initial.code);
  const formatError = codeChanged ? codeFormatError(form.code) : null;
  const { answer, check } = useCodeCheck({
    companyId: ownerCompany,
    excludeId: coupon?.id || null,
    code: form?.code || '',
    enabled: Boolean(open && codeChanged && !formatError && !archived),
  });
  const owner = answer && !answer.available && answer.usedby ? answer.usedby : null;
  const answerError = answer && !answer.available && !answer.usedby ? answer.message : null;
  const serverError = serverCodeError && form && serverCodeError.code === form.code ? serverCodeError.message : null;
  const codeError = formatError || answerError || serverError;
  const nameError = form && !form.name.trim() ? MESSAGES.name : null;
  const dirty = Boolean(form && initial) && JSON.stringify(form) !== JSON.stringify(initial);
  const blocked = saving || Object.keys(ruleErrors).length > 0 || Boolean(nameError || codeError || owner);

  // D3 in Edit: the saved store stays on offer while the item is the saved
  // one (an old campaign keeps "All stores"); a new item narrows the stores
  // to those that sell it, but only once the store list has answered: while it
  // is pending or refused the saved store stays on offer, so it is never
  // widened by a list that is not there (the api refuses a store that does
  // not sell the item, with its own sentence).
  const itemChanged =
    Boolean(initial && rule) && storesState === 'ready' && (initial.rule.itemId ?? null) !== (rule.itemId ?? null);
  const savedStore = initial ? initial.storeid ?? ALL_STORES : ALL_STORES;
  const storeOptions = useMemo(() => {
    if (!ruleType) return [];
    const strict = storeChoices(ruleType, product, stores);
    if (itemChanged || strict.some((option) => option.value === savedStore)) return strict;
    const label = savedStore === ALL_STORES ? 'All stores' : stores.find((store) => store.id === savedStore)?.name || EDIT_COPY.storeGone;
    return [{ value: savedStore, label }, ...strict];
  }, [ruleType, product, stores, itemChanged, savedStore]);

  useEffect(() => {
    // Before the stored row is read there are no options to pick from: the
    // saved store must not be read as "not offered" and widened to All stores.
    if (!initial) return;
    setForm((prev) => {
      if (!prev) return prev;
      const next = pickStore(storeOptions, prev.storeid);
      if (next === prev.storeid) return prev;
      return { ...prev, storeid: next, menuids: keepMenusOf(menus, next, prev.menuids) };
    });
    // `menus` is read for the prune and `initial` only gates the first run; this
    // effect answers the store options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeOptions]);

  const patch = (changes) => {
    setForm((prev) => ({ ...prev, ...changes }));
    setFooterError('');
  };
  const changeRule = (changes) => {
    setForm((prev) => ({ ...prev, rule: { ...prev.rule, ...changes } }));
    setFooterError('');
  };
  const changeStore = (value) => {
    setForm((prev) => ({ ...prev, storeid: value, menuids: keepMenusOf(menus, value, prev.menuids) }));
    setFooterError('');
  };
  const commitCode = (value) => {
    setServerCodeError(null);
    patch({ code: value });
  };

  // Spec §2.5: 2 shows the switch on and off writes 4; 1/3/4 show it off and
  // on writes 2. Back at the starting position is the stored status again.
  const initialOn = initial ? switchOn(initial.status) : false;
  const setActive = (on) => {
    setForm((prev) => ({ ...prev, status: on === initialOn ? initial.status : statusAfterSwitch(on) }));
    setFooterError('');
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirm({ kind: 'discard', owner: null });
    else onClose();
  };
  const openOwnerLink = (target) => setConfirm({ kind: 'discard', owner: target });
  const discard = () => {
    const target = confirm?.owner;
    setConfirm(null);
    onClose();
    if (target) onOpenOwner(target, form.code);
  };

  // `source` is the form for a save, the stored row for Archive (which
  // writes 5 from any status, D2, and never the unsaved edits).
  const write = async (status, source) => {
    if (saving) return;
    setSaving(true);
    setFooterError('');
    setServerCodeError(null);
    try {
      await publicCouponApi.upsert(upsertBody({ ...source, status }, { companyid: ownerCompany, id: coupon.id }));
      setSaving(false);
      setConfirm(null);
      onSaved();
      onClose();
    } catch (error) {
      setSaving(false);
      setConfirm(null);
      const text = apiMessage(error);
      // Pre-flight ruling R-14d: a refused save never leaves the switch where
      // the api did not take it (R-4a refuses a switch-on beside a case twin);
      // it goes back to the stored status. The refusal is shown below.
      if (source === form && form.status !== initial.status) {
        setForm((prev) => ({ ...prev, status: initial.status }));
      }
      if (source === form && (isTakenMessage(text) || CODE_FORMAT_MESSAGES.includes(text))) {
        setServerCodeError({ code: form.code, message: text });
        if (isTakenMessage(text)) check(form.code);
      } else {
        setFooterError(text || EDIT_COPY.saveFailed);
      }
    }
  };

  const ready = Boolean(form && initial);
  const created = coupon?.createdate ? dayjs(coupon.createdate) : null;
  const subtitle = [TYPE_LABELS[ruleType], created && created.isValid() ? `Created ${created.format('MMM D')}` : null]
    .filter(Boolean)
    .join(' · ');
  const summaryProduct = product || (names.product ? { name: names.product, items: [] } : null);
  const summary =
    viewRule && (viewRule.type === 'discount' || summaryProduct) ? summarySentence(viewRule, summaryProduct, item) : form?.name || '';
  const on = form ? switchOn(form.status) : false;
  const used = coupon?.usagecount ?? 0;
  const archiving = confirm?.kind === 'archive';

  let statusControl = null;
  if (archived) {
    statusControl = <FinBadge tone="canceled" label={EDIT_COPY.archived} />;
  } else if (draft) {
    statusControl = <FinBadge tone="canceled" label={EDIT_COPY.draft} />;
  } else if (ready) {
    statusControl = (
      <>
        <span className={cn('font-semibold', on ? 'text-fin-green' : 'text-fin-muted')}>{on ? EDIT_COPY.active : EDIT_COPY.inactive}</span>
        <Switch
          checked={on}
          onChange={setActive}
          aria-label={EDIT_COPY.switchLabel}
          className={cn('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors', on ? 'bg-fin-blue' : 'bg-fin-border-2')}
        >
          <span
            aria-hidden="true"
            className={cn('inline-block h-5 w-5 transform rounded-full bg-fin-surface shadow transition', on ? 'translate-x-[22px]' : 'translate-x-0.5')}
          />
        </Switch>
      </>
    );
  }

  let footer = null;
  if (archived) {
    footer = (
      <div className="flex justify-end">
        <FinButton onClick={onClose}>{EDIT_COPY.close}</FinButton>
      </div>
    );
  } else if (ready) {
    footer = (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={() => setConfirm({ kind: 'archive' })}
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-fin-red hover:underline disabled:opacity-60"
        >
          <ArchiveBoxIcon className="h-4 w-4" aria-hidden="true" />
          {EDIT_COPY.archive}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <FinButton disabled={saving} onClick={requestClose}>
            {EDIT_COPY.cancel}
          </FinButton>
          {draft ? (
            <>
              <FinButton disabled={blocked || !dirty} onClick={() => write(CAMPAIGN_STATUS.draft, form)}>
                {EDIT_COPY.saveDraft}
              </FinButton>
              <FinButton variant="primary" disabled={blocked} onClick={() => write(CAMPAIGN_STATUS.active, form)}>
                {EDIT_COPY.publish}
              </FinButton>
            </>
          ) : (
            <FinButton variant="primary" disabled={blocked || !dirty} onClick={() => write(form.status, form)}>
              {EDIT_COPY.save}
            </FinButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <TWModal
      open={open && ready}
      onClose={requestClose}
      size="lg"
      panelClassName="overflow-visible sm:!max-w-[880px]"
      bodyClassName="!p-0"
    >
      {ready ? (
        <div className="flex flex-col rounded-t-2xl bg-fin-surface sm:rounded-md">
          <div className="flex items-start justify-between gap-3 border-b border-fin-border px-6 py-5">
            <div className="min-w-0">
              <Dialog.Title as="h2" className="text-[18px] font-bold tracking-[-0.01em] text-fin-ink">
                {EDIT_COPY.title}
              </Dialog.Title>
              <p className="mt-0.5 text-[13px] text-fin-muted">{subtitle}</p>
            </div>
            <button
              type="button"
              aria-label={EDIT_COPY.close}
              onClick={requestClose}
              className="-m-1 rounded-md p-1 text-fin-muted hover:bg-fin-neutral-pale hover:text-fin-ink"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="grid sm:grid-cols-[minmax(0,1fr)_276px]">
            <div className="min-w-0 space-y-5 px-6 py-5">
              <section aria-labelledby="edit-campaign-rule">
                <h3 id="edit-campaign-rule" className="mb-2.5 text-[13.5px] font-bold text-fin-ink">
                  {EDIT_COPY.rule}
                </h3>
                <RuleSentence
                  variant="compact"
                  rule={viewRule}
                  onChange={changeRule}
                  products={typeProducts}
                  productsState={choicesState}
                  onRetryProducts={loadChoices}
                  errors={ruleErrors}
                  disabled={archived}
                  fallback={names}
                />
              </section>

              <section aria-labelledby="edit-campaign-basics" className="border-t border-fin-hair pt-5">
                <h3 id="edit-campaign-basics" className="mb-2.5 text-[13.5px] font-bold text-fin-ink">
                  {EDIT_COPY.basics}
                </h3>
                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
                  {/* The error sits outside the label, so the field's name stays
                      "Campaign name" while it shows. */}
                  <div className="space-y-1.5">
                    <label className="block space-y-1.5">
                      <span className="block text-[13px] font-semibold text-fin-ink">{EDIT_COPY.name}</span>
                      <input
                        type="text"
                        value={form.name}
                        disabled={archived}
                        aria-invalid={nameError ? true : undefined}
                        aria-describedby={nameError ? nameErrorId : undefined}
                        onChange={(event) => patch({ name: event.target.value })}
                        className={cn(
                          'h-10 w-full rounded-lg border bg-fin-surface px-3 text-[13.5px] text-fin-ink outline-none focus:border-fin-blue disabled:opacity-60',
                          nameError ? 'border-fin-red' : 'border-fin-border-2',
                        )}
                      />
                    </label>
                    {nameError ? (
                      <p id={nameErrorId} className="text-[12.5px] text-fin-red">
                        {nameError}
                      </p>
                    ) : null}
                  </div>
                  <CodeField
                    value={form.code}
                    onCommit={commitCode}
                    error={codeError}
                    owner={owner}
                    onOpenOwner={openOwnerLink}
                    disabled={archived}
                  />
                </div>
              </section>

              <AdvancedSettings
                open={advancedOpen}
                onToggle={() => setAdvancedOpen((prev) => !prev)}
                showStore={stores.length > 1}
                storeOptions={storeOptions}
                storeid={form.storeid}
                onStore={changeStore}
                platforms={form.platforms}
                onPlatforms={(value) => patch({ platforms: value })}
                menus={menusForStore(menus, form.storeid)}
                menuids={form.menuids}
                onMenus={(value) => patch({ menuids: value })}
                collapsedNote={
                  stores.length > 1 ? storeOptions.find((option) => option.value === (form.storeid ?? ALL_STORES))?.label || '' : ''
                }
                disabled={archived}
              />
            </div>

            <aside aria-label={EDIT_COPY.summary} className="order-first flex flex-col border-b border-fin-border bg-fin-recess px-6 py-5 sm:order-none sm:border-b-0 sm:border-l">
              <p className="text-[12.5px] font-medium text-fin-muted">{EDIT_COPY.summary}</p>
              <p className="mt-1 text-[17px] font-bold leading-snug text-fin-ink">{summary}</p>
              <span className="mt-2.5 inline-flex w-fit rounded-md border border-fin-border-2 bg-fin-surface px-2 py-0.5 font-mono text-[12px] font-semibold text-fin-ink">
                {form.code}
              </span>
              <dl className="mt-4 divide-y divide-fin-hair border-y border-fin-hair text-[13.5px]">
                <div className="flex items-center justify-between gap-3 py-2.5">
                  <dt className="text-fin-muted">{EDIT_COPY.status}</dt>
                  <dd className="flex items-center gap-2.5">{statusControl}</dd>
                </div>
                <div className="flex items-center justify-between gap-3 py-2.5">
                  <dt className="text-fin-muted">{EDIT_COPY.used}</dt>
                  <dd className="font-semibold tabular-nums text-fin-ink">{EDIT_COPY.times(used)}</dd>
                </div>
              </dl>
              <p className="mt-auto pt-6 text-[12.5px] leading-relaxed text-fin-muted">{EDIT_COPY.note}</p>
            </aside>
          </div>

          <div className="border-t border-fin-border bg-fin-recess px-6 py-4 sm:rounded-b-md">
            {footer}
            {footerError ? (
              <p role="alert" className="mt-2 text-[13px] text-fin-red">
                {footerError}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      <ConfirmDialog
        open={Boolean(confirm)}
        title={archiving ? EDIT_COPY.archiveTitle : EDIT_DISCARD_COPY.title}
        body={archiving ? EDIT_COPY.archiveBody : EDIT_DISCARD_COPY.body}
        confirmLabel={archiving ? EDIT_COPY.archive : EDIT_DISCARD_COPY.confirm}
        cancelLabel={archiving ? EDIT_COPY.cancel : EDIT_DISCARD_COPY.cancel}
        danger
        busy={saving}
        onConfirm={archiving ? () => write(CAMPAIGN_STATUS.archived, initial) : discard}
        onCancel={() => setConfirm(null)}
      />
    </TWModal>
  );
};

export default EditCampaignModal;
